import {
  AppstoreOutlined,
  BlockOutlined,
  BookOutlined,
  ControlOutlined,
  DollarOutlined,
  HomeOutlined,
  LogoutOutlined,
  RiseOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  UserOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router';

import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useMenuToggle } from '@client/store/menu.store';
import { ERoutePaths } from '@client/types';
import { getTranslatedMessage } from '@client/utils/i18n.util';

import type { AuthContextType } from '@client/context/AuthContext';
import type { MenuProps } from 'antd';
import type { ItemType, MenuItemType } from 'antd/es/menu/interface';

type MenuItem = Required<MenuProps>['items'][number];

interface LevelKeysProps {
  key?: string;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  children?: LevelKeysProps[];
}

export type TUseMenu = {
  mItems: MenuItem[];
  selectedMenuKeys: string[];
  openedMenuKeys: string[];
  onOpenChange: (openKeys: string[]) => void;
};

/**
 * Generates an array of menu items based on the given i18n object, ability and navigate function.
 * The ability object is used to determine which menu items should be disabled.
 * The navigate function is used to navigate to the correct page when a menu item is clicked.
 * @param {AuthContextType} auth - The authentication context.
 * @param {NavigateFunction} navigate - The navigate function.
 * @param {(menuOpened: boolean) => void} setPanelOpen - Function to set the drawer panel open state.
 * @returns {MenuItem[]} An array of menu items.
 */
export const menuItems = (
  auth: AuthContextType,
  navigate: NavigateFunction,
  setPanelOpen: (menuOpened: boolean) => void,
): MenuItem[] => {
  const dHome = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.HOME,
  );
  const dDashboard = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.DASHBOARD,
  );
  const dClients = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.TENANTS,
  );
  const dFeatures = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.FEATURES,
  );
  const dSubscriptions = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.SUBSCRIPTIONS,
  );
  const dMarketplace = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.MARKETPLACE,
  );
  const dCredits = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.CREDITS,
  );
  const dRevenue = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.REVENUE,
  );
  const dEntitlements = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.ENTITLEMENTS,
  );
  const dUsers = auth.ability.cannot(EAbilityActions.MANAGE, auth.user?.uid);
  const dLogout = auth.ability.cannot(
    EAbilityActions.READ,
    EAbilitySubjects.LOGOUT,
  );

  const _childLabel = (
    label: string,
    path: string,
    onClick?: (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => Promise<void> | void,
  ): React.ReactNode => (
    <a
      href={path}
      rel="noopener noreferrer"
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();

        await navigate(path as ERoutePaths);

        if (onClick) {
          await onClick(e);
        }

        setPanelOpen(false);
      }}
    >
      {getTranslatedMessage(label)}
    </a>
  );

  const _child = (
    key: string,
    label: string,
    icon: React.ReactNode,
    path: string,
    disabled: boolean,
    onClick?: (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => Promise<void> | void,
  ): MenuItem => ({
    key,
    icon,
    disabled,
    label: _childLabel(label, path, onClick),
  });

  const _home = {
    ..._child('home', 'menu.home', <HomeOutlined />, ERoutePaths.HOME, dHome),
  } as ItemType<MenuItemType>;

  const _dashboard = {
    ..._child(
      'dashboard',
      'menu.dashboard',
      <BlockOutlined />,
      ERoutePaths.DASHBOARD,
      dDashboard,
    ),
  } as ItemType<MenuItemType>;

  const _clients = {
    ..._child(
      'tenants',
      'menu.tenants',
      <AppstoreOutlined />,
      ERoutePaths.TENANTS,
      dClients,
    ),
  } as ItemType<MenuItemType>;

  const _features = {
    ..._child(
      'features',
      'menu.features',
      <ControlOutlined />,
      ERoutePaths.FEATURES,
      dFeatures,
    ),
  } as ItemType<MenuItemType>;

  const _subscriptions = {
    ..._child(
      'subscriptions',
      'menu.subscriptions',
      <ShoppingCartOutlined />,
      ERoutePaths.SUBSCRIPTIONS,
      dSubscriptions,
    ),
  } as ItemType<MenuItemType>;

  const _marketplace = {
    ..._child(
      'admin-marketplace',
      'menu.adminMarketplace',
      <ShopOutlined />,
      ERoutePaths.ADMIN_MARKETPLACE,
      dMarketplace,
    ),
  } as ItemType<MenuItemType>;

  const _marketplaceFeatures = {
    ..._child(
      'admin-marketplace-features',
      'menu.adminMarketplaceFeatures',
      <ShopOutlined />,
      ERoutePaths.ADMIN_MARKETPLACE_FEATURES,
      dMarketplace,
    ),
  } as ItemType<MenuItemType>;

  const _credits = {
    ..._child(
      'credits',
      'menu.credits',
      <DollarOutlined />,
      ERoutePaths.CREDITS,
      dCredits,
    ),
  } as ItemType<MenuItemType>;

  const _revenue = {
    ..._child(
      'revenue',
      'menu.revenue',
      <RiseOutlined />,
      ERoutePaths.REVENUE,
      dRevenue,
    ),
  } as ItemType<MenuItemType>;

  const _entitlements = {
    ..._child(
      'entitlements',
      'menu.entitlements',
      <SettingOutlined />,
      ERoutePaths.ENTITLEMENTS,
      dEntitlements,
    ),
  } as ItemType<MenuItemType>;

  const _profile = {
    ..._child(
      'profile',
      'menu.profile',
      <UserOutlined />,
      ERoutePaths.PROFILE,
      dUsers,
    ),
  } as ItemType<MenuItemType>;

  const _logout = {
    ..._child(
      'logout',
      'actions.logout',
      <LogoutOutlined />,
      null,
      dLogout,
      async (e) => {
        e.preventDefault();
        e.stopPropagation();

        await auth.signOut();
      },
    ),
  } as ItemType<MenuItemType>;

  const pageChildren: ItemType<MenuItemType>[] = [
    _dashboard,
    _clients,
    _features,
    _subscriptions,
  ];

  if (!dMarketplace) {
    pageChildren.push(_marketplace);
    pageChildren.push(_marketplaceFeatures);
  }
  if (!dCredits) {
    pageChildren.push(_credits);
  }
  if (!dRevenue) {
    pageChildren.push(_revenue);
  }
  if (!dEntitlements) {
    pageChildren.push(_entitlements);
  }

  return [
    _home,
    {
      key: 'pages',
      icon: <BookOutlined />,
      label: getTranslatedMessage('menu.pages'),
      children: pageChildren,
    },
    {
      key: 'management',
      icon: <SettingOutlined />,
      label: getTranslatedMessage('menu.management'),
      children: [_profile, _logout],
    },
  ];
};

/**
 * Generate a record of menu item keys to their levels.
 * @param {LevelKeysProps[]} items1 - Menu items.
 * @returns {Record<string, number>} - A record of menu item keys to their levels.
 */
const getLevelKeys = (items1: LevelKeysProps[]): Record<string, number> => {
  const key: Record<string, number> = {};

  /**
   * Recursively assigns a level number to each menu item's key.
   * @param {LevelKeysProps[]} items2 - The list of menu items to process.
   * @param {number} [level] - The current level of menu items being processed.
   * Each item's key is mapped to this level.
   * Defaults to 1 for the top-level items.
   */
  const func = (items2: LevelKeysProps[], level: number = 1): void => {
    items2.forEach((item) => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };

  func(items1);

  return key;
};

/**
 * Get the selected menu item keys based on the current pathname.
 * @param {MenuItem[]} mItems - The menu items.
 * @param {string} pathname - The current pathname.
 * @returns {string[]} The selected menu item keys.
 */
const getSelectedKeys = (mItems: MenuItem[], pathname: string): string[] => {
  type TMatcherItem = {
    key: string | number;
    children?: TMatcherItem[] | undefined;
    label?: string | undefined;
  };

  const hrefOf = (item: TMatcherItem): string | null => {
    try {
      return item && item['label'] && item['label']['props']
        ? String(item['label']['props']['href'] ?? '')
        : null;
    } catch {
      return null;
    }
  };

  const pathMatches = (path: string, href: string | null): boolean => {
    if (!href) return false;
    if (href === '/') return path === '/';
    return path === href || path.startsWith(href + '/');
  };

  const findMatch = (
    items: TMatcherItem[],
    path: string,
    parents: string[] = [],
  ): string[] | null => {
    for (const item of items) {
      const key = item.key?.toString();
      const children = (item as TMatcherItem).children as
        TMatcherItem[] | undefined;
      if (children && children.length) {
        const res = findMatch(
          children,
          path,
          key ? [...parents, key] : parents,
        );
        if (res) return res;
      } else {
        const href = hrefOf(item);
        if (pathMatches(path, href)) {
          return key ? [...parents, key] : [...parents];
        }
      }
    }
    return null;
  };

  return findMatch(mItems as unknown as TMatcherItem[], pathname, []) ?? [];
};

/**
 * Returns the menu items, selected menu item keys, opened menu item keys and the onOpenChange handler.
 * The menu items are generated based on the given i18n object, ability and navigate function.
 * The selected menu item keys are determined based on the current pathname.
 * The opened menu item keys are determined based on the selected menu item keys.
 * The onOpenChange handler is used to handle the openChange event.
 * @param {boolean} isOpen - Whether the menu is open.
 * @returns {TUseMenu} The menu items, selected menu item keys, opened menu item keys and the onOpenChange handler.
 */
export const useMenu = (isOpen?: boolean): TUseMenu => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { setMenuOpened } = useMenuToggle();

  const [mItems, setMItems] = useState<ItemType[]>([]);
  const [selectedMenuKeys, setSelectedMenuKeys] = useState<string[]>([]);
  const [openedMenuKeys, setOpenedMenuKeys] = useState<string[]>([]);

  const { pathname } = useLocation();

  useEffect(() => {
    if (isOpen) setMItems(menuItems(auth, navigate, setMenuOpened));
  }, [auth, isOpen, navigate, setMenuOpened]);

  const levelKeys = getLevelKeys(mItems as LevelKeysProps[]);

  useEffect(() => {
    if (isOpen) {
      const selectedKeys = getSelectedKeys(mItems, pathname);

      setSelectedMenuKeys(selectedKeys);
      setOpenedMenuKeys(selectedKeys);
    }
  }, [mItems, pathname, isOpen]);

  /**
   * Function to handle openChange event.
   * When a menu item is opened, it will remove the key of the same level
   * and all child keys from the stateOpenKeys.
   * When a menu item is closed, it will add all of its child keys to the stateOpenKeys.
   */
  const onOpenChange: MenuProps['onOpenChange'] =
    useCallback(
      (openKeys: string[]): void => {
        const currentOpenKey = openKeys.find(
          (key: string) => openedMenuKeys.indexOf(key) === -1,
        );

        if (currentOpenKey !== undefined) {
          const repeatIndex = openKeys
            .filter((key) => key !== currentOpenKey)
            .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);

          setOpenedMenuKeys(
            openKeys
              .filter((_, index) => index !== repeatIndex)
              .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]),
          );
        } else {
          setOpenedMenuKeys(openKeys);
        }
      },
      [levelKeys, openedMenuKeys],
    ) ??
    (() => {
      /* empty */
    });

  return { mItems, selectedMenuKeys, openedMenuKeys, onOpenChange };
};
